import React, { useState, Suspense, memo, useMemo, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { ENABLE_AI_DESIGN_STUDIO } from './config';
import ProtectedRoute from './components/ProtectedRoute';
import SidebarNavigation from './components/SidebarNavigation';
import TopNavigation from './components/TopNavigation';
import SkipLink from './components/SkipLink';
import RouteAnnouncer from './components/RouteAnnouncer';
import NotificationSystem, { useNotifications } from './components/NotificationSystem';
import { UpgradePromptModal } from './components/subscription';
import FeatureProtectedRoute from './components/subscription/FeatureProtectedRoute';
import TrialBanner from './components/subscription/TrialBanner';
import GracePeriodBanner from './components/subscription/GracePeriodBanner';
import ErrorBoundary from './components/ErrorBoundary';
import ConfirmModal from './components/ConfirmModal';
import GlobalJobProgress from './components/GlobalJobProgress';
import useDataStore from './stores/dataStore';
import useUploadJobsWatcher from './hooks/useUploadJobsWatcher';
import useAuthBootstrap from './hooks/useAuthBootstrap';
import useRouteAnnouncer from './hooks/useRouteAnnouncer';
import { MAIN_CONTENT_ID, ROUTE_CHROME_ATTRIBUTE } from './constants/a11y';

// Critical components (loaded immediately)
import LoginRegister from './pages/LoginRegister';
import OAuthRedirect from './pages/OAuthRedirect';
import LandingPage from './pages/LandingPage';

// Public docs page for agent-key holders (sellers / their technical contacts) —
// lazy-loaded since it's off the beaten path, but never behind ProtectedRoute.
const AgentApiDocsPage = React.lazy(() => import('./pages/AgentApiDocsPage'));

// Lazy loaded components for better performance
const MockupCreator = React.lazy(() => import('./pages/MockupCreator'));
const MockupRecreateJob = React.lazy(() => import('./pages/MockupRecreateJob'));
const MockupRecreateJobs = React.lazy(() => import('./pages/MockupRecreateJobs'));
const Account = React.lazy(() => import('./pages/Account'));
const PrinterManagement = React.lazy(() => import('./pages/PrinterManagement'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const SubscriptionPage = React.lazy(() => import('./pages/SubscriptionPage'));
const CheckoutPage = React.lazy(() => import('./pages/Subscription/CheckoutPage'));
const PaymentSuccessPage = React.lazy(() => import('./pages/Subscription/PaymentSuccessPage'));
const PaymentCanceledPage = React.lazy(() => import('./pages/Subscription/PaymentCanceledPage'));
const ToolsTab = React.lazy(() => import('./pages/HomeTabs/ToolsTab'));

// Shopify components (lazy loaded)
const ShopifyConnect = React.lazy(() => import('./pages/ShopifyConnect'));
const ShopifySuccess = React.lazy(() => import('./pages/ShopifySuccess'));
const ShopifyProducts = React.lazy(() => import('./pages/ShopifyProducts'));
const ShopifyOrders = React.lazy(() => import('./pages/ShopifyOrders'));
const ShopifyDashboard = React.lazy(() => import('./pages/ShopifyDashboard'));
const ShopifyAnalytics = React.lazy(() => import('./components/ShopifyAnalytics'));
const ShopifyMigrationExceptions = React.lazy(() => import('./pages/Shopify/MigrationExceptions'));
const ShopifyTemplateCreator = React.lazy(() => import('./pages/ShopifyTemplateCreator'));

// CraftFlow Commerce components (lazy loaded)
const CraftFlowDashboard = React.lazy(() => import('./pages/CraftFlowDashboard'));
const CraftFlowProducts = React.lazy(() => import('./pages/CraftFlowProducts'));
const CraftFlowTemplateCreator = React.lazy(() => import('./pages/CraftFlowTemplateCreator'));
const CraftFlowProductCreator = React.lazy(() => import('./pages/CraftFlowProductCreator'));
const CraftFlowOrders = React.lazy(() => import('./pages/CraftFlowOrders'));
const CraftFlowOrderDetails = React.lazy(() => import('./pages/CraftFlowOrderDetails'));
const CraftFlowCustomers = React.lazy(() => import('./pages/CraftFlowCustomers'));
const CraftFlowCustomerDetails = React.lazy(() => import('./pages/CraftFlowCustomerDetails'));
const CraftFlowStorefrontSettings = React.lazy(() => import('./pages/CraftFlowStorefrontSettings'));
const CraftFlowBuilderSettings = React.lazy(() => import('./pages/CraftFlowBuilderSettings'));
const CraftFlowGangSheetOrders = React.lazy(() => import('./pages/CraftFlowGangSheetOrders'));
const CraftFlowGangSheetOrderDetail = React.lazy(() => import('./pages/CraftFlowGangSheetOrderDetail'));
const CraftFlowGalleryManager = React.lazy(() => import('./pages/CraftFlowGalleryManager'));
const CraftFlowExportJobs = React.lazy(() => import('./pages/CraftFlowExportJobs'));
const CraftFlowGangsheetBuilder = React.lazy(() => import('./pages/CraftFlowGangsheetBuilder'));
// Consolidated sections: each hosts its former standalone pages as tabs.
const CraftFlowEmails = React.lazy(() => import('./pages/CraftFlowEmails'));
const CraftFlowPages = React.lazy(() => import('./pages/CraftFlowPages'));
const CraftFlowDiscounts = React.lazy(() => import('./pages/CraftFlowDiscounts'));
const CraftFlowSales = React.lazy(() => import('./pages/CraftFlowSales'));
const CraftFlowGiftCards = React.lazy(() => import('./pages/CraftFlowGiftCards'));
const CraftFlowPriceLists = React.lazy(() => import('./pages/CraftFlowPriceLists'));
const CraftFlowProspects = React.lazy(() => import('./pages/CraftFlowProspects'));
const CraftFlowInvoices = React.lazy(() => import('./pages/CraftFlowInvoices'));
const CraftFlowCollections = React.lazy(() => import('./pages/CraftFlowCollections'));
const CraftFlowAnalytics = React.lazy(() => import('./pages/CraftFlowAnalytics'));
const CraftFlowReviews = React.lazy(() => import('./pages/CraftFlowReviews'));
const CraftFlowMessages = React.lazy(() => import('./pages/CraftFlowMessages'));

// Etsy components (lazy loaded)
const EtsyDashboard = React.lazy(() => import('./pages/Etsy/Dashboard'));
const EtsyOrders = React.lazy(() => import('./pages/Etsy/Orders'));
const EtsyCustomers = React.lazy(() => import('./pages/Etsy/Customers'));
const EtsyAnalytics = React.lazy(() => import('./pages/Etsy/Analytics'));
const EtsyListings = React.lazy(() => import('./pages/Etsy/Listings'));

// CraftFlow Pages (lazy loaded)
const CraftFlowPageEditor = React.lazy(() => import('./pages/CraftFlow/Pages/PageEditor'));

// CraftFlow Email Management components (lazy loaded)
const CraftFlowEmailTemplateEditor = React.lazy(() => import('./pages/CraftFlow/Emails/EmailTemplateEditor'));

// CraftFlow Marketing Assets (lazy loaded)
const CraftFlowMarketingAssets = React.lazy(() => import('./pages/CraftFlow/Marketing/MarketingAssets'));

// AX (Agent Experience) components (lazy loaded)
const AXContracts = React.lazy(() => import('./pages/AX/Contracts'));
const AXRules = React.lazy(() => import('./pages/AX/Rules'));
const AXArtifacts = React.lazy(() => import('./pages/AX/Artifacts'));
const AXSkills = React.lazy(() => import('./pages/AX/Skills'));

// AI Design Studio (lazy loaded)
const AiDesignStudio = React.lazy(() => import('./pages/AiDesignStudio'));

// Credits page (lazy loaded)
const CreditsPage = React.lazy(() => import('./pages/CreditsPage'));

// Template components (lazy loaded)
const TemplateCreator = React.lazy(() => import('./pages/TemplateCreator'));

// Account status pages (lazy loaded)
const AccountSuspendedPage = React.lazy(() => import('./pages/AccountSuspendedPage'));

// Loading component for suspense fallback
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
    <span className="ml-3 text-gray-600">Loading...</span>
  </div>
);

// Error boundary to handle chunk load failures (happens after deployments)
class ChunkLoadErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Check if it's a chunk load error
    const isChunkLoadError =
      error.name === 'ChunkLoadError' ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Loading CSS chunk');

    if (isChunkLoadError) {
      // Clear any cached data and reload
      // Prevent infinite reload loop
      const lastReload = sessionStorage.getItem('lastChunkErrorReload');
      const now = Date.now();

      if (!lastReload || now - parseInt(lastReload) > 10000) {
        sessionStorage.setItem('lastChunkErrorReload', now.toString());
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      const isChunkLoadError =
        this.state.error?.name === 'ChunkLoadError' || this.state.error?.message?.includes('Loading chunk');

      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-4">
          <div className="text-center max-w-md">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              {isChunkLoadError ? 'App Updated' : 'Something went wrong'}
            </h2>
            <p className="text-gray-600 mb-4">
              {isChunkLoadError
                ? 'A new version is available. Please refresh to continue.'
                : 'An error occurred while loading this page.'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Smart home route: landing page for visitors, dashboard for authenticated users
const SmartHome = () => {
  const { isUserAuthenticated } = useAuth();
  if (isUserAuthenticated) {
    return <Navigate to="/etsy/dashboard" replace />;
  }
  return <LandingPage />;
};

const AppLayout = memo(({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { notifications, removeNotification } = useNotifications();

  // Per-route document.title, focus on the new page's heading and the polite live-region
  // text. AppLayout owns the single <main id="main-content">, the skip link and the
  // announcer; pages must never render their own <main>.
  const { announcement } = useRouteAnnouncer();

  // App-level watcher for in-flight Etsy bulk-upload jobs. Polls the
  // server while any are active and toasts on terminal status — lets the
  // ProductUploadModal close immediately after submit and notify later.
  // Internally checks auth + sleeps when no active jobs, so mounting it
  // here on every page is cheap.
  useUploadJobsWatcher();

  // Root-only auth bootstrap: fires the verify-Etsy-connection +
  // timezone-sync side effects exactly once per state change. These used
  // to live in ``useAuth`` itself, but 28 components mount that hook so
  // each auth flip fanned out into N parallel HTTP calls. Mounting the
  // bootstrap only here, on the single ``AppLayout`` instance, collapses
  // that to one call per state change.
  useAuthBootstrap();

  // Get orders data from store to calculate active orders count
  // Note: storeData.orders already contains only active orders (paid, not shipped, not canceled)
  const { data: storeData } = useDataStore();

  // Active orders count (orders are already filtered in the API fetch)
  const activeOrdersCount = useMemo(() => {
    const orders = storeData.orders || [];
    return orders.length;
  }, [storeData.orders]);

  // Hide sidebar and top nav on login, landing, and public pages
  const isPublicPage = useMemo(
    () =>
      ['/', '/login', '/oauth/redirect', '/welcome', '/account-suspended', '/docs/ai-agents'].includes(
        location.pathname
      ),
    [location.pathname]
  );

  // Memoize handlers to prevent unnecessary re-renders
  const toggleSidebar = useCallback(() => setSidebarOpen(prev => !prev), []);

  const handleTabChange = useCallback(
    tabId => {
      const newParams = new URLSearchParams(searchParams);
      const currentMainTab = searchParams.get('tab');

      if (currentMainTab === 'orders' || currentMainTab === 'products') {
        newParams.set('subtab', tabId);
      } else {
        newParams.set('tab', tabId);
        newParams.delete('subtab'); // Clear subtab when changing main tab
      }

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  // Memoize current tab values
  const { currentTab, currentSubTab } = useMemo(
    () => ({
      currentTab: searchParams.get('tab') || 'overview',
      currentSubTab: searchParams.get('subtab'),
    }),
    [searchParams]
  );

  if (isPublicPage) {
    // Landing page and the agent-docs page both paint their own full-bleed
    // background (min-h-screen bg-white); other public pages use the gradient.
    const hasOwnBackground = location.pathname === '/' || location.pathname === '/docs/ai-agents';
    return (
      <div
        className={
          hasOwnBackground
            ? ''
            : 'min-h-screen bg-gradient-to-br from-lavender-100 via-mint-50 to-peach-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800'
        }
      >
        <SkipLink />
        <RouteAnnouncer message={announcement} />
        <main id={MAIN_CONTENT_ID} tabIndex={-1} className="focus:outline-hidden">
          {children}
        </main>
        <NotificationSystem notifications={notifications} onDismiss={removeNotification} />
      </div>
    );
  }

  return (
    <div className="h-screen overflow-hidden relative bg-gradient-to-br from-sage-50 via-mint-25 to-lavender-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      <SkipLink />
      <RouteAnnouncer message={announcement} />

      {/* Sidebar Navigation */}
      <SidebarNavigation isOpen={sidebarOpen} onToggle={toggleSidebar} />

      {/* Main Content Area — fixed-height column so only <main> scrolls,
          the sidebar + top nav stay put. */}
      <div className="lg:ml-64 h-screen flex flex-col overflow-hidden">
        {/* Top Navigation */}
        <TopNavigation
          activeTab={currentSubTab || currentTab}
          onTabChange={handleTabChange}
          onMenuToggle={toggleSidebar}
          unshippedOrdersCount={activeOrdersCount}
        />

        {/* Page Content — the only scroll container */}
        <main
          id={MAIN_CONTENT_ID}
          tabIndex={-1}
          className="flex-1 overflow-y-auto min-h-0 p-4 lg:p-8 focus:outline-hidden"
        >
          <div className="max-w-7xl mx-auto">
            {/* Persistent chrome, not page content: the route announcer ignores it when it decides
                whether a page has rendered. display:contents keeps the banners laid out exactly
                as direct children of this column. */}
            <div className="contents" {...{ [ROUTE_CHROME_ATTRIBUTE]: '' }}>
              <TrialBanner />
              <GracePeriodBanner />
            </div>
            {children}
          </div>
        </main>
      </div>

      {/* Notification System */}
      <NotificationSystem notifications={notifications} onDismiss={removeNotification} />

      {/* Global Upgrade Prompt Modal */}
      <UpgradePromptModal />
    </div>
  );
});

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AppLayout>
          <ChunkLoadErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<LoginRegister />} />
                <Route path="/welcome" element={<Navigate to="/" replace />} />
                <Route path="/oauth/redirect" element={<OAuthRedirect />} />
                <Route path="/account-suspended" element={<AccountSuspendedPage />} />
                {/*
                  Public setup guide for whoever holds a minted agent (`cfk_`) key —
                  a seller or their technical contact, not necessarily a CraftFlow
                  user. Linked from the super-admin AgentsPanel so the key issuer can
                  hand this URL to the key holder. Must stay unauthenticated.
                */}
                <Route path="/docs/ai-agents" element={<AgentApiDocsPage />} />

                {/* Home - Landing page for visitors, dashboard redirect for users */}
                <Route path="/" element={<SmartHome />} />

                {/* Mockup Creator */}
                <Route
                  path="/mockup-creator"
                  element={
                    <ProtectedRoute>
                      <MockupCreator />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/mockups/recreate"
                  element={
                    <ProtectedRoute>
                      <MockupRecreateJobs />
                    </ProtectedRoute>
                  }
                />

                {/* Regenerate-mockups job (#1792): progress, review, publish / undo */}
                <Route
                  path="/mockups/recreate/:jobId"
                  element={
                    <ProtectedRoute>
                      <MockupRecreateJob />
                    </ProtectedRoute>
                  }
                />

                {/* AI Design Studio (hidden behind feature flag) */}
                {ENABLE_AI_DESIGN_STUDIO && (
                  <Route
                    path="/ai-design"
                    element={
                      <ProtectedRoute>
                        <AiDesignStudio />
                      </ProtectedRoute>
                    }
                  />
                )}

                {/*
                  Credits is a SELLER billing surface — balance, transaction
                  history, POST /api/v1/credits/purchase — and `credits_router`
                  is mounted bare in server/src/api.py with a plain CurrentUser,
                  deliberately outside the `_administration` dependency list.
                  It was gated on super_admin only because its URL starts with
                  /admin, which locked every seller out of their own billing.
                  The test is "is this Administration?", not "does the path
                  start with /admin?".
                */}
                <Route
                  path="/admin/credits"
                  element={
                    <ProtectedRoute>
                      <CreditsPage />
                    </ProtectedRoute>
                  }
                />

                {/* Shop Management Routes */}
                {/*
                  /orders and /listings used to mount OrdersTab / nothing at
                  all. OrdersTab with no props has no way to know whether Etsy
                  is connected or to offer Connect, so the route rendered a
                  page that could only misreport itself; /listings rendered an
                  empty ProtectedRoute. Nothing links to either — the sidebar
                  points at /etsy/*, which is where the prop-wired pages live —
                  so redirect rather than 404 any bookmark that survives.
                */}
                <Route path="/orders" element={<Navigate to="/etsy/orders" replace />} />
                <Route path="/listings" element={<Navigate to="/etsy/listings" replace />} />
                {/* `/analytics` used to render <HomeTabs/AnalyticsTab> with NO props,
                    so `isConnected` was always undefined and every visitor — connected
                    or not — got "Please connect your Etsy shop" above a Connect button
                    whose onClick was undefined. It now redirects like its two
                    neighbours, to the page that actually has this data. */}
                <Route path="/analytics" element={<Navigate to="/etsy/analytics" replace />} />

                {/* Tools & Settings */}
                <Route
                  path="/tools"
                  element={
                    <ProtectedRoute>
                      <ToolsTab />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/account"
                  element={
                    <ProtectedRoute>
                      <Account />
                    </ProtectedRoute>
                  }
                />

                {/* Etsy Routes */}
                <Route
                  path="/etsy/dashboard"
                  element={
                    <ProtectedRoute>
                      <EtsyDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/etsy/orders"
                  element={
                    <ProtectedRoute>
                      <EtsyOrders />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/etsy/customers"
                  element={
                    <ProtectedRoute>
                      <EtsyCustomers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/etsy/analytics"
                  element={
                    <ProtectedRoute>
                      <EtsyAnalytics />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/etsy/listings"
                  element={
                    <ProtectedRoute>
                      <EtsyListings />
                    </ProtectedRoute>
                  }
                />

                {/* Shopify Routes */}
                <Route
                  path="/shopify/connect"
                  element={
                    <ProtectedRoute>
                      <ShopifyConnect />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/shopify/success"
                  element={
                    <ProtectedRoute>
                      <ShopifySuccess />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/shopify/dashboard"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="shopify_integration">
                        <ShopifyDashboard />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/shopify/products"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="shopify_integration">
                        <ShopifyProducts />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/templates/create"
                  element={
                    <ProtectedRoute>
                      <TemplateCreator />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/shopify/templates/create"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="shopify_integration">
                        <ShopifyTemplateCreator />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/shopify/templates/:id/edit"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="shopify_integration">
                        <ShopifyTemplateCreator />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/templates/create"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowTemplateCreator />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/shopify/orders"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="shopify_integration">
                        <ShopifyOrders />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                {/* Was pointed at <ShopifyDashboard/>, so the dedicated analytics
                    component was imported by nothing and unreachable. */}
                <Route
                  path="/shopify/analytics"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="shopify_integration">
                        <ShopifyAnalytics />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />

                {/* Shopify Migration Exceptions */}
                <Route
                  path="/shopify/migration/exceptions"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="shopify_integration">
                        <ShopifyMigrationExceptions />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />

                {/* CraftFlow Commerce Routes */}
                <Route
                  path="/craftflow/dashboard"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowDashboard />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/products"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowProducts />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/products/create"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowProductCreator />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/products/edit/:id"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowProductCreator />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/orders"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowOrders />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/orders/:id"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowOrderDetails />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/customers"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowCustomers />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/customers/:id"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowCustomerDetails />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/settings"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowStorefrontSettings />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/domain"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <Navigate to="/craftflow/settings?tab=domain" replace />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/builder-settings"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowBuilderSettings />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/gangsheet-builder"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowGangsheetBuilder />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/gang-sheets"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowGangSheetOrders />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/gang-sheets/:id"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowGangSheetOrderDetail />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/gallery"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowGalleryManager />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/export-jobs"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowExportJobs />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/discounts"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowDiscounts />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/sales"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowSales />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/gift-cards"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowGiftCards />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/price-lists"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowPriceLists />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/prospects"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowProspects />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/invoices"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowInvoices />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/collections"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowCollections />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/analytics"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowAnalytics />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/reviews"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowReviews />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/messages"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowMessages />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                {/* Redirects is now a tab inside Pages; the old path is kept for bookmarks. */}
                <Route path="/craftflow/redirects" element={<Navigate to="/craftflow/pages?tab=redirects" replace />} />

                {/* CraftFlow Pages — the section itself; Pages + Redirects are its tabs. */}
                <Route
                  path="/craftflow/pages"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowPages />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/pages/new"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowPageEditor />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/pages/:id/edit"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowPageEditor />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />

                {/* CraftFlow Email Management — one section, the five pages are its tabs. */}
                <Route
                  path="/craftflow/emails"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowEmails />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                {/* The old per-page paths are kept for bookmarks and for the template
                    editor's back-navigation; each lands on the matching tab. */}
                <Route
                  path="/craftflow/emails/templates"
                  element={<Navigate to="/craftflow/emails?tab=templates" replace />}
                />
                <Route
                  path="/craftflow/emails/templates/new"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowEmailTemplateEditor />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/craftflow/emails/templates/edit/:id"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowEmailTemplateEditor />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route path="/craftflow/emails/logs" element={<Navigate to="/craftflow/emails?tab=logs" replace />} />
                <Route
                  path="/craftflow/emails/analytics"
                  element={<Navigate to="/craftflow/emails?tab=analytics" replace />}
                />
                <Route
                  path="/craftflow/emails/subscribers"
                  element={<Navigate to="/craftflow/emails?tab=subscribers" replace />}
                />
                <Route
                  path="/craftflow/emails/campaigns"
                  element={<Navigate to="/craftflow/emails?tab=campaigns" replace />}
                />
                <Route
                  path="/craftflow/marketing/assets"
                  element={
                    <ProtectedRoute>
                      <FeatureProtectedRoute feature="craftflow_commerce">
                        <CraftFlowMarketingAssets />
                      </FeatureProtectedRoute>
                    </ProtectedRoute>
                  }
                />

                {/* AX (Agent Experience) Routes */}
                <Route
                  path="/ax/skills"
                  element={
                    <ProtectedRoute>
                      <AXSkills />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ax/contracts"
                  element={
                    <ProtectedRoute>
                      <AXContracts />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ax/rules"
                  element={
                    <ProtectedRoute>
                      <AXRules />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ax/artifacts"
                  element={
                    <ProtectedRoute>
                      <AXArtifacts />
                    </ProtectedRoute>
                  }
                />

                {/* Printer Management */}
                <Route
                  path="/printers"
                  element={
                    <ProtectedRoute>
                      <PrinterManagement />
                    </ProtectedRoute>
                  }
                />

                {/* Administration — super_admin only, matching the /admin/* API gate. */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />

                {/* Subscription Management */}
                <Route
                  path="/subscription"
                  element={
                    <ProtectedRoute>
                      <SubscriptionPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/subscription/checkout"
                  element={
                    <ProtectedRoute>
                      <CheckoutPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/subscription/success"
                  element={
                    <ProtectedRoute>
                      <PaymentSuccessPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/subscription/canceled"
                  element={
                    <ProtectedRoute>
                      <PaymentCanceledPage />
                    </ProtectedRoute>
                  }
                />

                {/* 404 Not Found */}
                <Route
                  path="*"
                  element={
                    <div className="flex flex-col items-center justify-center min-h-[60vh] p-4">
                      <h1 className="text-4xl font-bold text-gray-800 mb-2">404</h1>
                      <p className="text-gray-600 mb-4">Page not found</p>
                      <a
                        href="/"
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                      >
                        Go Home
                      </a>
                    </div>
                  }
                />
              </Routes>
            </Suspense>
          </ChunkLoadErrorBoundary>
        </AppLayout>
      </Router>
      <ConfirmModal />
      <GlobalJobProgress />
    </ErrorBoundary>
  );
}

export { AppLayout };
export default App;
